import React, { useState, useRef, useLayoutEffect } from 'react'
import '../../../styles/common/ImageWithPlaceholder.css'

// The frame reserves the image's space up front, so the skeleton and the
// loaded image occupy exactly the same box and nothing below them moves.
// 273x215 is the feed thumbnail; other call sites scale from their `width`.
const DEFAULT_RATIO = 273 / 215

/**
 * Two layouts:
 *
 *  - default: the component owns the box. Pass `width` (and optionally
 *    `height` or `ratio`); the image is absolutely positioned inside it, so
 *    the space is reserved before the image arrives and nothing shifts.
 *
 *  - `fill`: the call site's own container already defines the box (the
 *    legacy `.image-container*` / `.card-image` rules do). The image stays in
 *    normal flow so those screens keep the height they have today, and
 *    `minHeight` holds the box open while the skeleton is up.
 *
 * Pass `src` to use a URL verbatim, or `url` to have the `&width=` resize
 * parameter appended.
 */
const ImageWithPlaceholder = ({
    url,
    src: srcProp,
    width = 125,
    height,
    altText = '',
    ratio = DEFAULT_RATIO,
    className = '',
    fill = false,
    minHeight,
}) => {
    const src = srcProp ?? (url.includes('width') ? url : url + '&width=' + width)
    const imgRef = useRef(null)
    const [status, setStatus] = useState('loading')

    // A cached image can finish decoding before React attaches `onLoad`, which
    // would mount and unmount the skeleton within one frame and flash. Reading
    // `complete` before paint skips the skeleton entirely in that case.
    // Keyed on `src` so a recycled row in the infinite-scroll list resets
    // instead of showing the previous post's image.
    useLayoutEffect(() => {
        const node = imgRef.current
        if (node?.complete) {
            setStatus(node.naturalWidth > 0 ? 'loaded' : 'error')
        } else {
            setStatus('loading')
        }
    }, [src])

    let frameStyle
    if (fill) {
        // Released on load so the container falls back to the height the
        // image gives it, exactly as it did before.
        frameStyle = status === 'loaded' ? undefined : { minHeight }
    } else {
        // Resolved to pixels rather than handed to `aspect-ratio`: React 17
        // does not treat aspectRatio as unitless and would emit `1.269...px`,
        // which is invalid, leaving the frame zero-height because everything
        // in it is absolutely positioned.
        const h = height ?? Math.round(width / ratio)
        frameStyle = { width, minWidth: width, maxWidth: width, height: h, maxHeight: h }
    }

    return (
        <div
            className={`iwp-frame ${fill ? 'iwp-frame--fill' : ''} ${className}`}
            style={frameStyle}
            data-status={status}
            aria-busy={status === 'loading'}
        >
            {status !== 'error' && <div className="iwp-skeleton" aria-hidden="true" />}
            {status === 'error' ? (
                <div className="iwp-error" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="m21 15-4.5-4.5L3 21" />
                    </svg>
                </div>
            ) : (
                <img
                    ref={imgRef}
                    src={src}
                    alt={altText}
                    className="iwp-img task-image"
                    data-loaded={status === 'loaded'}
                    // Safe only because the frame reserves its space: lazy
                    // loading into an unsized box is what makes lists jump.
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setStatus('loaded')}
                    onError={() => setStatus('error')}
                />
            )}
        </div>
    )
}

export default ImageWithPlaceholder
